import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useGetLegacyPnlQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtUsd } from "@/lib/format";

function Stat({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "good" | "bad";
}) {
  const color =
    tone === "good"
      ? "text-emerald-400"
      : tone === "bad"
      ? "text-rose-400"
      : "text-foreground";
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
        <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
        {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function Legacy() {
  const { data, isLoading, isError } = useGetLegacyPnlQuery();

  // Append the final "lost it all -> $0" point so the curve shows the wipeout.
  const curve = useMemo(() => {
    const bals = data?.balance_history ?? [];
    if (!bals.length) return [] as Array<{ ts: string; balance: number }>;
    const pts = bals.map((b) => ({ ts: b.bal_date, balance: b.balance_usd }));
    const last = bals[bals.length - 1];
    // synthetic wipeout marker one day after the last snapshot
    pts.push({ ts: `${last.bal_date}·lost`, balance: 0 });
    return pts;
  }, [data]);

  if (isLoading) {
    return <div className="p-6 text-muted">Loading legacy account…</div>;
  }
  if (isError || !data?.available) {
    return (
      <div className="p-6 text-muted">
        Legacy account data isn’t loaded. Run{" "}
        <code className="text-foreground">scripts/load_legacy_pnl.py</code> to
        populate it.
      </div>
    );
  }

  const s = data.summary;
  const netIn = s.net_funded_in_usd;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Legacy Account</h1>
        <p className="text-sm text-muted">
          Pre-bot trading account (2020–2025), funded from PKR. Peaked at{" "}
          {fmtUsd(s.peak_balance_usd)} on {s.peak_balance_date}, then fully lost.
          Separate from the live bot portfolio — a lifetime baseline only.
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Realised P&L"
          value={fmtUsd(s.realised_pnl_usd)}
          sub="final $0 − net funded in"
          tone={s.realised_pnl_usd >= 0 ? "good" : "bad"}
        />
        <Stat
          label="Net Funded In"
          value={fmtUsd(netIn)}
          sub={`PKR ${s.net_funded_in_pkr.toLocaleString()}`}
        />
        <Stat
          label="Peak Balance"
          value={fmtUsd(s.peak_balance_usd)}
          sub={s.peak_balance_date ?? undefined}
        />
        <Stat
          label="Final Value"
          value={fmtUsd(s.final_value_usd)}
          sub={`last snap ${fmtUsd(s.last_balance_usd)} · ${s.last_balance_date}`}
          tone="bad"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Stat label="Total Deposited" value={fmtUsd(s.total_deposited_usd)} tone="good" />
        <Stat label="Total Withdrawn" value={fmtUsd(s.total_withdrawn_usd)} tone="bad" />
        <Stat
          label="Records"
          value={`${s.balance_count} snaps`}
          sub={`${s.flow_count} funding flows`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Equity Curve</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart data={curve} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="legacyEq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="ts"
                stroke="hsl(var(--muted))"
                fontSize={11}
                tickFormatter={(t: string) => t.slice(0, 7)}
                minTickGap={40}
              />
              <YAxis
                stroke="hsl(var(--muted))"
                fontSize={11}
                tickFormatter={(v: number) => `$${v.toLocaleString()}`}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--bg-2))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 6,
                  fontSize: 12,
                }}
                formatter={(v) => fmtUsd(typeof v === "number" ? v : Number(v))}
                labelFormatter={(l) => String(l).replace("·lost", " (lost it all)")}
              />
              {/* Net capital funded in — above this line was profit, below was loss */}
              <ReferenceLine
                y={netIn}
                stroke="hsl(var(--muted))"
                strokeDasharray="4 4"
                label={{
                  value: `funded in ${fmtUsd(netIn)}`,
                  position: "insideTopRight",
                  fill: "hsl(var(--muted))",
                  fontSize: 11,
                }}
              />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="hsl(var(--accent))"
                fill="url(#legacyEq)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-muted">
            Dashed line = net capital funded in ({fmtUsd(netIn)}). The balance
            crossed above it during the 2023 peak, then bled out to zero.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
